import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles, Check } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { HERO_IMG } from "@/lib/constants";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export function Hero({ onApply }) {
  const ref = useRef(null);
  const { t } = useLang();
  const [foundingLeft, setFoundingLeft] = useState(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => setFoundingLeft(data.founding_left)).catch(() => {});
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]" />
        <div className="absolute inset-0 spotlight" />
      </motion.div>

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-10 pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 mb-8"
        >
          <Sparkles size={14} className="text-white" />
          <span className="text-xs font-medium tracking-[0.15em] uppercase text-zinc-300">{t("hero.badge")}</span>
        </motion.div>

        <h1 className="font-display font-black tracking-[-0.03em] leading-[0.92] text-white text-[13vw] sm:text-6xl md:text-7xl lg:text-[5.6rem] max-w-5xl">
          <MaskedLines lines={t("hero.title")} delay={0.25} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 max-w-xl text-lg md:text-xl text-zinc-400 leading-relaxed"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-8 flex flex-wrap gap-2.5"
          data-testid="hero-badges"
        >
          {(t("hero.badges") || []).map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur px-3.5 py-1.5 text-xs font-medium text-zinc-200">
              <Check size={13} className="text-emerald-400" /> {b}
            </span>
          ))}
          <span data-testid="hero-founding-badge" className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3.5 py-1.5 text-xs font-bold text-amber-300">
            {foundingLeft != null ? t("hero.foundingLeft").replace("{n}", foundingLeft) : t("hero.foundingDefault")}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-apply-btn"
            onClick={onApply}
            className="group rounded-full bg-white text-black font-semibold text-base px-8 py-4 hover:bg-zinc-200 active:scale-95 transition-[background-color,transform] duration-300 flex items-center gap-2"
          >
            {t("hero.apply")}
            <ArrowDown size={18} className="group-hover:translate-y-0.5 transition-transform duration-300" />
          </button>
          <a href="#program" className="text-sm text-zinc-400 hover:text-white transition-colors underline underline-offset-4 decoration-zinc-700">
            {t("hero.discover")}
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-zinc-500">
        <span className="text-[10px] uppercase tracking-[0.3em]">{t("hero.scroll")}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.div>
      </div>
    </section>
  );
}
